import { lowerCamelCase, object, pluralize, present, upperCamelCase } from '@art-suite/art-core-ts';
import { DndContext, DragEndEvent, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import { ApiScanTemplate } from '@kaiizen/kai-pro-api';
import { createTemplateGroup, ScanTemplateGroup, ScanTemplateRoot, ScanTemplateTask } from '@kaiizen/kai-scan-template-system';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import searchIcon from '../../Assets/search-v2.svg';
import { ScanTemplateDataMutators } from '../../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { AddButton } from '../../Widgets';

const DEFAULT_CATEGORY_NAME = 'other';

const getCategorizedTemplateGroups = (groups: ScanTemplateGroup[]): Record<string, ScanTemplateGroup[]> => {
  const categorizedGroups: Record<string, ScanTemplateGroup[]> = {
    interior: [],
    exterior: [],
    [DEFAULT_CATEGORY_NAME]: [],
  };

  groups?.forEach(group => {
    const category = lowerCamelCase(group.category || DEFAULT_CATEGORY_NAME);
    categorizedGroups[category] = [...(categorizedGroups[category] || []), group];
  });

  return categorizedGroups;
};

const SCAN_TEMPLATE_NAV_PANEL_STYLE = css`
  label: SCAN_TEMPLATE_NAV_PANEL_STYLE;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS2.alwaysDark.background.dark};
  height: 100%;
  gap: ${LAYOUT2.spacing1}px;
`;

const SEARCH_OUTER_CONTAINER_STYLE = css`
  label: SEARCH_OUTER_CONTAINER_STYLE;
  padding: 0 ${LAYOUT2.spacing2}px;
`;

const SEARCH_CONTAINER_STYLE = css`
  label: SEARCH_CONTAINER_STYLE;
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${COLORS2.alwaysDark.background.light};
  padding: 0 ${LAYOUT2.spacing2}px;
  height: ${LAYOUT2.spacing5}px;
`;

const SEARCH_INPUT_STYLE = css`
  label: SEARCH_INPUT_STYLE;
  flex: 1;
  padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing6}px ${LAYOUT2.spacing1}px 0;
  background: transparent;
  border: none;
  outline: none;
  height: 45px;
  color: ${COLORS2.alwaysDark.text.white};
  ${TYPOGRAPHY2.xSmall};
  ::placeholder {
    color: ${COLORS2.alwaysDark.text.darkGray};
  }
`;

const SEARCH_ICON_STYLE = css`
  label: SEARCH_ICON_STYLE;
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  width: 45px;
  height: 45px;
`;

const CLEAR_BUTTON_STYLE = css`
  label: CLEAR_BUTTON_STYLE;
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${COLORS2.alwaysDark.text.darkGray};
  width: 45px;
  height: 45px;

  &:hover {
    color: ${COLORS2.alwaysDark.text.white};
  }
`;

const SearchIcon = ({ color = COLORS2.alwaysDark.text.darkGray, size = 30 }: { color?: string; size?: number }) => (
  <img src={searchIcon} width={size} height={size} alt='Search' />
);

const ClearIcon = ({ color = COLORS2.alwaysDark.text.darkGray, size = 20 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='18' y1='6' x2='6' y2='18' />
    <line x1='6' y1='6' x2='18' y2='18' />
  </svg>
);

const GROUP_ITEM_STYLE = css`
  label: GROUP_ITEM_STYLE;
  ${TYPOGRAPHY2.xSmall};
  display: flex;
  align-items: center;
  margin: 0 ${LAYOUT2.spacing2}px 0 ${LAYOUT2.spacing2}px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  height: ${LAYOUT2.spacing1 * 3.5}px;
  color: ${COLORS2.alwaysDark.text.green};
  border-bottom: 1px solid ${COLORS2.alwaysDark.border.darkGray};

  &:hover {
    background-color: ${COLORS2.alwaysDark.background.black};
  }

  &:active {
    cursor: grabbing;
  }
`;

const GROUP_ITEM_DRAGGING_STYLE = css`
  label: GROUP_ITEM_DRAGGING_STYLE;
  opacity: 0.5;
`;

const DRAG_HANDLE_STYLE = css`
  label: DRAG_HANDLE_STYLE;
  display: flex;
  align-items: center;
  color: ${COLORS2.alwaysDark.text.darkGray};
  pointer-events: none;
`;

const CURRENT_GROUP_STYLE = css`
  label: CURRENT_GROUP_STYLE;
  border-left: 8px solid ${COLORS2.alwaysDark.primary};
  padding-left: 8px;
  color: ${COLORS2.alwaysDark.text.white};
`;

const GROUP_TITLE_STYLE = css`
  label: GROUP_TITLE_STYLE;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GROUP_TASK_COUNT_STYLE = css`
  label: GROUP_TASK_COUNT_STYLE;
  ${TYPOGRAPHY2.xSmall};
  color: ${COLORS2.alwaysDark.text.darkGray};
  margin-left: ${LAYOUT2.spacing1}px;
`;

const GROUPS_LIST_STYLE = css`
  label: GROUPS_LIST_STYLE;
  flex: 1;
  overflow-y: auto;
`;

const CATEGORY_DIVIDER_CONTAINER_STYLE = css`
  label: CATEGORY_DIVIDER_CONTAINER_STYLE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${LAYOUT2.spacing2}px 0 ${LAYOUT2.spacingHalf}px 0;
  background-color: ${COLORS2.alwaysDark.background.dark};
  margin: 0 ${LAYOUT2.spacing2}px 0 ${LAYOUT2.spacing2}px;
  border-bottom: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const CATEGORY_DIVIDER_STYLE = css`
  label: CATEGORY_DIVIDER_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  color: ${COLORS2.alwaysDark.text.darkGray};
  text-transform: uppercase;
`;

const TEMPLATE_OVERVIEW_BOX_STYLE = css`
  label: TEMPLATE_OVERVIEW_BOX_STYLE;
  display: flex;
  border: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  overflow: hidden;
  margin: 0 ${LAYOUT2.spacing2}px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${COLORS2.alwaysDark.background.black};
  }
`;

const DRAGGING_OVERLAY_WRAPPER_STYLE = css`
  label: DRAGGING_OVERLAY_WRAPPER_STYLE;
  width: 210px;
`;

const TEMPLATE_OVERVIEW_LEFT_BORDER_STYLE = css`
  label: TEMPLATE_OVERVIEW_LEFT_BORDER_STYLE;
  width: 8px;
  background-color: ${COLORS2.alwaysDark.primary};
`;

const TEMPLATE_OVERVIEW_CONTENT_STYLE = css`
  label: TEMPLATE_OVERVIEW_CONTENT_STYLE;
  flex: 1;
  padding: ${LAYOUT2.spacing1p5}px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TEMPLATE_OVERVIEW_TITLE_STYLE = css`
  label: TEMPLATE_OVERVIEW_TITLE_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  color: ${COLORS2.alwaysDark.text.white};
`;

const GroupMenuItem = ({
  group,
  isCurrentGroup,
  setCurrentGroupId,
  isDragEnabled,
  filteredTaskIds,
}: {
  group: ScanTemplateGroup;
  isCurrentGroup: boolean;
  setCurrentGroupId: (groupId: string) => void;
  isDragEnabled: boolean;
  filteredTaskIds: string[];
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id, disabled: !isDragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      css={[
        GROUP_ITEM_STYLE,
        isCurrentGroup && CURRENT_GROUP_STYLE,
        isDragging && GROUP_ITEM_DRAGGING_STYLE,
        !isDragEnabled && css`cursor: pointer;`,
      ]}
      {...(isDragEnabled ? { ...attributes, ...listeners } : {})}
      onClick={() => setCurrentGroupId(group.id)}
    >
      <div css={GROUP_TITLE_STYLE}>
        {group.name}
      </div>
      <div css={GROUP_TASK_COUNT_STYLE}>
        {pluralize(filteredTaskIds.length, 'task')}
      </div>
      {isDragEnabled && (
        <div css={DRAG_HANDLE_STYLE}>
          <DragIndicatorIcon fontSize='small' />
        </div>
      )}
    </div>
  );
};

const TemplateOverviewMenuItem = ({
  scanTemplate,
  isCurrentView,
  onSelect,
}: {
  scanTemplate: ApiScanTemplate;
  isCurrentView: boolean;
  onSelect: () => void;
}) => (
  <div
    css={TEMPLATE_OVERVIEW_BOX_STYLE}
    onClick={onSelect}
  >
    {isCurrentView && <div css={TEMPLATE_OVERVIEW_LEFT_BORDER_STYLE} />}
    <div css={TEMPLATE_OVERVIEW_CONTENT_STYLE}>
      <div css={TEMPLATE_OVERVIEW_TITLE_STYLE}>Template Overview</div>
    </div>
  </div>
);

const AllTasksMenuItem = ({
  isSelected,
  taskCount,
  filteredTaskCount,
  onSelect,
}: {
  isSelected: boolean;
  taskCount: number;
  filteredTaskCount: number;
  onSelect: () => void;
}) => (
  <div
    css={TEMPLATE_OVERVIEW_BOX_STYLE}
    onClick={onSelect}
  >
    {isSelected && <div css={TEMPLATE_OVERVIEW_LEFT_BORDER_STYLE} />}
    <div css={TEMPLATE_OVERVIEW_CONTENT_STYLE}>
      <div css={TEMPLATE_OVERVIEW_TITLE_STYLE}>
        All Tasks ({filteredTaskCount && filteredTaskCount !== taskCount ? `${filteredTaskCount} of ${taskCount}` : taskCount})
      </div>
    </div>
  </div>
);

const moveGroupInTemplate = (template: ScanTemplateRoot, groupId: string, newIndex: number): ScanTemplateRoot => {
  const groups = template.groupTemplates ?? [];
  const oldIndex = groups.findIndex(g => g.id === groupId);
  if (oldIndex === -1 || oldIndex === newIndex) return template;

  const reorderedGroups = [...groups];
  const [movedGroup] = reorderedGroups.splice(oldIndex, 1);
  reorderedGroups.splice(newIndex, 0, movedGroup);

  return { ...template, groupTemplates: reorderedGroups };
};

export const ScanTemplateNavPanel = ({
  filteredTasks,
  filter,
  filteredGroups,
  filteredTasksById,
  filterRegex,
  groupId,
  scanTemplate,
  scanTemplateDataMutators,
  setCurrentGroupId,
  setFilter,
  setShowingAllTasks,
  setShowingTemplateOverview,
  showAllTasks,
  templateRoot,
  readOnly,
}: {
  filteredTasks: ScanTemplateTask[];
  filter: string;
  filteredGroups: ScanTemplateGroup[];
  filteredTasksById: Record<string, ScanTemplateTask>;
  filterRegex?: RegExp;
  groupId?: string;
  scanTemplate: ApiScanTemplate;
  scanTemplateDataMutators: ScanTemplateDataMutators;
  setCurrentGroupId: (groupId: string | undefined) => void;
  setFilter: (filter: string) => void;
  setShowingAllTasks: () => void;
  setShowingTemplateOverview: () => void;
  showAllTasks?: boolean;
  templateRoot: ScanTemplateRoot;
  readOnly?: boolean;
}) => {
  const navigate = useNavigate();
  const [draggingGroupId, setDraggingGroupId] = useState<string | null>(null);
  // keep the searchCache here so we don't have a memory leak for long-running sessions; within one template edit session it shouldn't be too bad
  const groups = templateRoot.groupTemplates || [];
  const filteredTasksByGroupIds = useMemo(() =>
    object(filteredGroups, {
      withKey: ({ id }) => id,
      with: (group: ScanTemplateGroup) => group.taskTemplateIds?.filter((id: string) => !!filteredTasksById[id]) ?? [],
    }), [filteredGroups, filteredTasksById]);
  const [orderedGroupIds, setOrderedGroupIds] = useState<string[]>(() => groups.map(g => g.id));
  const groupIds = groups.map(g => g.id);
  const groupIdsKey = groupIds.join(',');
  // keep ordered ids in sync with template updates (e.g., template loads or external changes)
  // groupIdsKey used intentionally: groupIds is a new array every render and would cause infinite loop
  useEffect(() => setOrderedGroupIds(groupIds), [groupIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const filterEnabled = present(filterRegex);
  // apply ordering to filtered groups
  const visibleGroups = useMemo(
    () => filteredGroups.sort((a, b) => orderedGroupIds.indexOf(a.id) - orderedGroupIds.indexOf(b.id)),
    [filteredGroups, orderedGroupIds],
  );
  const categorizedGroups = useMemo(() => getCategorizedTemplateGroups(visibleGroups), [visibleGroups]);
  const [localFilter, setLocalFilter] = useState(filter);
  useEffect(() => setLocalFilter(filter), [filter]);
  const debouncedSetFilter = useDebouncedCallback(setFilter, 500);

  const taskCount = templateRoot.taskTemplatesById ? Object.keys(templateRoot.taskTemplatesById).length : 0;

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 8 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 250, tolerance: 8 },
  });
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggingGroupId(null);

    if (!over || active.id === over.id) return;

    // Compute indices from current ordered ids to avoid snap-back
    const oldIndex = orderedGroupIds.indexOf(active.id as string);
    const newIndex = orderedGroupIds.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      setOrderedGroupIds(ids => arrayMove(ids, oldIndex, newIndex));
      scanTemplateDataMutators.updateTemplate(moveGroupInTemplate(templateRoot, active.id as string, newIndex));
    }
  };

  const draggingGroup = draggingGroupId ? groups.find(g => g.id === draggingGroupId) : null;
  const isDragEnabled = !filterEnabled && !readOnly;

  return (
    <div css={SCAN_TEMPLATE_NAV_PANEL_STYLE}>
      <div css={SEARCH_OUTER_CONTAINER_STYLE}>
        <div css={SEARCH_CONTAINER_STYLE}>
          <input
            css={SEARCH_INPUT_STYLE}
            value={localFilter}
            onChange={e => {
              setLocalFilter(e.target.value);
              debouncedSetFilter(e.target.value);
            }}
            placeholder='filter all'
          />
          {filter
            ? (
              <button css={CLEAR_BUTTON_STYLE} onClick={() => setFilter('')} aria-label='Clear search'>
                <ClearIcon />
              </button>
            )
            : (
              <span css={SEARCH_ICON_STYLE}>
                <SearchIcon />
              </span>
            )}
        </div>
      </div>
      <TemplateOverviewMenuItem
        scanTemplate={scanTemplate}
        isCurrentView={groupId === undefined && !showAllTasks}
        onSelect={setShowingTemplateOverview}
      />
      <AllTasksMenuItem
        isSelected={!!showAllTasks}
        taskCount={taskCount}
        filteredTaskCount={filteredTasks.length}
        onSelect={setShowingAllTasks}
      />
      <div css={GROUPS_LIST_STYLE}>
        {Object.keys(categorizedGroups).sort().map(category => {
          const categoryGroups = categorizedGroups[category];

          return (
            <div key={category}>
              <div css={CATEGORY_DIVIDER_CONTAINER_STYLE}>
                <div css={CATEGORY_DIVIDER_STYLE}>
                  {pluralize(categoryGroups.length, `${filterEnabled ? 'matching ' : ''}${upperCamelCase(category, ' ')} group`)}
                </div>
                {!readOnly && (
                  <AddButton
                    small
                    onClick={() => {
                      const newGroup = createTemplateGroup({
                        name: `New ${upperCamelCase(category, ' ')} Group`,
                        category: category === DEFAULT_CATEGORY_NAME ? undefined : category,
                      });
                      scanTemplateDataMutators.addGroup(newGroup);
                      setTimeout(() => {
                        navigate(`/template/${scanTemplate.id}/group/${newGroup.id}?groupId=${newGroup.id}`);
                      }, 100);
                    }}
                  />
                )}
              </div>
              <DndContext
                sensors={sensors}
                onDragStart={event => setDraggingGroupId(event.active.id as string)}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={categoryGroups.map(g => g.id)} strategy={verticalListSortingStrategy}>
                  {categoryGroups.map(group => (
                    <GroupMenuItem
                      filteredTaskIds={filteredTasksByGroupIds[group.id] ?? []}
                      key={group.id}
                      group={group}
                      isCurrentGroup={group.id === groupId}
                      setCurrentGroupId={setCurrentGroupId}
                      isDragEnabled={isDragEnabled}
                    />
                  ))}
                </SortableContext>
                <DragOverlay dropAnimation={null}>
                  {draggingGroup && lowerCamelCase(draggingGroup.category || DEFAULT_CATEGORY_NAME) === category && (
                    <GroupMenuItem
                      filteredTaskIds={filteredTasksByGroupIds[draggingGroup.id] ?? []}
                      group={draggingGroup}
                      isCurrentGroup={draggingGroupId === groupId}
                      setCurrentGroupId={setCurrentGroupId}
                      isDragEnabled={isDragEnabled}
                    />
                  )}
                </DragOverlay>
              </DndContext>
            </div>
          );
        })}
      </div>
    </div>
  );
};
